import { clamp } from '@/lib/math'

/** What the preloader knows at a moment in time. */
export interface PreloadState {
  /** Seconds since the preloader mounted. */
  elapsed: number
  /** The shortest the preloader may show (the intro needs a beat). */
  minDuration: number
  /** Hard cap: past this the preloader leaves whatever is still loading. */
  maxDuration: number
  fontsReady: boolean
  heroReady: boolean
  /** `false` when there is no hero scene to wait for (no canvas, no WebGL2). */
  heroRequired: boolean
}

/** Default timings in seconds. Reduced motion keeps the wait but drops the theatre. */
export const PRELOAD_TIMING = {
  full: { minDuration: 1.6, maxDuration: 6 },
  reduced: { minDuration: 0.6, maxDuration: 6 },
} as const

/** Fraction of "real" progress that each job is worth when the hero scene is awaited. */
const FONT_WEIGHT = 0.3
/** Simulated creep toward the missing work: never stalls, never reaches 100 on its own. */
const CREEP_CEILING = 0.85
const CREEP_SECONDS = 2.5

/** Whether everything the preloader waits for is done (or the cap has passed). */
export function isPreloadComplete(state: PreloadState): boolean {
  if (state.elapsed >= state.maxDuration) return true
  return (
    state.elapsed >= state.minDuration &&
    state.fontsReady &&
    (!state.heroRequired || state.heroReady)
  )
}

/**
 * Target progress (0..1) blending real readiness with eased, simulated progress.
 *
 * - Time eases out toward the minimum duration, so the counter never sprints to 100.
 * - The unfinished share creeps forward exponentially, so it never visibly stalls.
 * - It reaches exactly 1 only when {@link isPreloadComplete} is true.
 *
 * Display code damps toward this value, so a job finishing late reads as a smooth
 * acceleration rather than a jump.
 */
export function preloadTarget(state: PreloadState): number {
  if (isPreloadComplete(state)) return 1
  const real = state.heroRequired
    ? FONT_WEIGHT * Number(state.fontsReady) + (1 - FONT_WEIGHT) * Number(state.heroReady)
    : Number(state.fontsReady)
  const time = clamp(state.elapsed / state.minDuration)
  const eased = 1 - (1 - time) * (1 - time)
  const creep = CREEP_CEILING * (1 - Math.exp(-state.elapsed / CREEP_SECONDS))
  // Below 1 until complete: the last percent belongs to the real finish.
  return Math.min(0.99, eased * (real + (1 - real) * creep))
}

/** The counter text: "000" … "100". */
export function formatCounter(progress: number): { zeros: string; digits: string } {
  const value = Math.floor(clamp(progress) * 100 + 1e-6)
  const text = String(value).padStart(3, '0')
  const firstDigit = text.search(/[1-9]/)
  if (firstDigit === -1) return { zeros: text, digits: '' }
  return { zeros: text.slice(0, firstDigit), digits: text.slice(firstDigit) }
}
